import { formatTrainingDateShortLabel } from "~/utils/trainingDateLabel";

export const DEFAULT_TIMEZONE = "Europe/Moscow";

/** IANA-идентификаторы часовых поясов РФ (федеральные). */
export const RUSSIAN_FEDERAL_TIMEZONE_IDS = [
  "Europe/Kaliningrad",
  "Europe/Moscow",
  "Europe/Samara",
  "Asia/Yekaterinburg",
  "Asia/Omsk",
  "Asia/Krasnoyarsk",
  "Asia/Irkutsk",
  "Asia/Yakutsk",
  "Asia/Vladivostok",
  "Asia/Magadan",
  "Asia/Kamchatka",
] as const;

const RUSSIAN_TIMEZONE_CITY_NAMES: Record<string, string> = {
  "Europe/Moscow": "Москва",
  "Europe/Kaliningrad": "Калининград",
  "Europe/Samara": "Самара",
  "Asia/Yekaterinburg": "Екатеринбург",
  "Asia/Omsk": "Омск",
  "Asia/Krasnoyarsk": "Красноярск",
  "Asia/Irkutsk": "Иркутск",
  "Asia/Yakutsk": "Якутск",
  "Asia/Vladivostok": "Владивосток",
  "Asia/Magadan": "Магадан",
  "Asia/Kamchatka": "Камчатка",
  "Europe/Kiev": "Киев",
  "Europe/Minsk": "Минск",
  "Asia/Almaty": "Алматы",
  "Asia/Tbilisi": "Тбилиси",
  "Asia/Tashkent": "Ташкент",
  "Europe/London": "Лондон",
  "America/New_York": "Нью-Йорк",
  "America/Chicago": "Чикаго",
  "America/Denver": "Денвер",
  "America/Anchorage": "Анкоридж",
  "America/Adak": "Адак",
  "America/Sao_Paulo": "Сан-Паулу",
  "Atlantic/Reykjavik": "Исландия",
  "Asia/Kolkata": "Индия",
  "Australia/Adelaide": "Аделаида",
  "Pacific/Honolulu": "Гонолулу",
  "Pacific/Pago_Pago": "Pago Pago",
  "Asia/Dubai": "Дубай",
  "Asia/Bangkok": "Бангкок",
  "Asia/Singapore": "Сингапур",
  "Asia/Tokyo": "Токио",
  "Australia/Sydney": "Сидней",
};

function resolveTimezoneCityLabelRu(timezone: string): string {
  const mapped = RUSSIAN_TIMEZONE_CITY_NAMES[timezone];
  if (mapped) {
    return mapped;
  }

  if (RUSSIAN_FEDERAL_TIMEZONE_IDS.includes(timezone as typeof RUSSIAN_FEDERAL_TIMEZONE_IDS[number])) {
    const city = timezone.split("/").pop()?.replace(/_/g, " ");
    return city || timezone;
  }

  const city = timezone.split("/").pop()?.replace(/_/g, " ");
  return city || timezone;
}

function formatUtcOffsetForLabel(timezone: string, now = new Date()): string {
  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(now);
    const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "";
    const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
    if (match) {
      const hours = match[2].padStart(2, "0");
      const minutes = (match[3] ?? "00").padStart(2, "0");
      return `${match[1]}${hours}:${minutes}`;
    }
  } catch {
    // ignore
  }

  return "+00:00";
}

export type DualScheduleTimeLabel = {
  primary: string;
  secondary: string;
  secondaryDate: string;
  timezonesDiffer: boolean;
  datesDiffer: boolean;
  primaryTzLabel: string;
  secondaryTzLabel: string;
};

export function normalizeIanaTimezone(value: unknown): string | null {
  const timezone = String(value ?? "").trim();
  if (!timezone) {
    return null;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return timezone;
  } catch {
    return null;
  }
}

export function resolveDefaultTimezone(): string {
  return DEFAULT_TIMEZONE;
}

export function resolveViewerTimezone(authUser: { timezone?: string | null } | null | undefined): string {
  return normalizeIanaTimezone(authUser?.timezone) ?? resolveDefaultTimezone();
}

export function resolveTodayYmd(timezone: string): string {
  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function resolveUtcOffsetMinutes(
  timezone: string,
  now: Date = new Date(),
): number {
  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(now);
    const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "";
    const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
    if (match) {
      const sign = match[1] === "-" ? -1 : 1;
      const hours = Number.parseInt(match[2], 10);
      const minutes = Number.parseInt(match[3] ?? "0", 10);
      return sign * (hours * 60 + minutes);
    }
  } catch {
    // ignore
  }

  return 0;
}

/** Один пункт на UTC-смещение: при дубликатах — федеральный пояс РФ из списка options. */
export function resolveCanonicalTimezoneForOption(
  timezone: string,
  options: Array<{ value: string }> = [],
): string {
  const normalized = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  if (options.some((option) => option.value === normalized)) {
    return normalized;
  }

  const offset = resolveUtcOffsetMinutes(normalized);
  const rfMatch = RUSSIAN_FEDERAL_TIMEZONE_IDS.find(
    (id) => resolveUtcOffsetMinutes(id) === offset
      && options.some((option) => option.value === id),
  );
  if (rfMatch) {
    return rfMatch;
  }

  const sameOffset = options.find((option) => resolveUtcOffsetMinutes(option.value) === offset);
  return sameOffset?.value ?? normalized;
}

export function formatTimezoneLabel(
  timezone: string,
  now: Date = new Date(),
): string {
  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  try {
    const city = resolveTimezoneCityLabelRu(tz);
    const offset = formatUtcOffsetForLabel(tz, now);
    return `${city}, UTC${offset}`;
  } catch {
    return tz;
  }
}

export function formatTimezoneBadge(timezone: string): string {
  return formatTimezoneLabel(timezone);
}

/** Текущее локальное время в IANA-часовом поясе, например «17:55». */
export function formatTimezoneCurrentTime(
  timezone: string,
  now: Date = new Date(),
  locale = "ru-RU",
): string {
  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(now);
  } catch {
    return "";
  }
}

/**
 * Разница UTC-смещения other относительно viewer: «+2 ч», «−1 ч», «+30 мин».
 * Пустая строка, если смещения совпадают (для тренера показ избыточен).
 */
export function formatTimezoneOffsetDiffLabel(
  otherTimezone: string,
  viewerTimezone: string,
  now: Date = new Date(),
): string {
  const other = normalizeIanaTimezone(otherTimezone);
  const viewer = normalizeIanaTimezone(viewerTimezone);
  if (!other || !viewer) {
    return "";
  }

  const diff = resolveUtcOffsetMinutes(other, now) - resolveUtcOffsetMinutes(viewer, now);
  if (diff === 0) {
    return "";
  }

  const sign = diff > 0 ? "+" : "−";
  const abs = Math.abs(diff);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;

  if (hours > 0 && minutes > 0) {
    return `${sign}${hours} ч ${minutes} мин`;
  }
  if (hours > 0) {
    return `${sign}${hours} ч`;
  }
  return `${sign}${minutes} мин`;
}

/** Совпадает ли текущее UTC-смещение (практически одно и то же локальное время). */
export function isSameUtcOffset(
  left: string | null | undefined,
  right: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const a = normalizeIanaTimezone(left);
  const b = normalizeIanaTimezone(right);
  if (!a || !b) {
    return false;
  }
  return resolveUtcOffsetMinutes(a, now) === resolveUtcOffsetMinutes(b, now);
}

type TimezoneParty = {
  iana?: string | null;
  label?: string | null;
  local_date?: string | null;
  local_time?: string | null;
};

export function formatLocalTimeFromParty(party: TimezoneParty | null | undefined): string {
  const time = String(party?.local_time ?? "").trim();
  if (time) {
    return time.slice(0, 5);
  }
  return "";
}

export function resolvePartyScheduleTime(
  party: TimezoneParty | null | undefined,
  scheduledAtUtc: string | null | undefined,
): string {
  const direct = formatLocalTimeFromParty(party);
  if (direct) {
    return direct;
  }

  const iana = normalizeIanaTimezone(party?.iana);
  if (scheduledAtUtc && iana) {
    const parts = localPartsFromUtc(scheduledAtUtc, iana);
    if (parts?.time) {
      return parts.time.slice(0, 5);
    }
  }

  return "";
}

type WorkoutTimezoneContextSource = {
  schedule_context?: {
    timezone_context?: {
      viewer?: TimezoneParty | null;
      client?: TimezoneParty | null;
      trainer?: TimezoneParty | null;
      canonical?: TimezoneParty | null;
      scheduled_at_utc?: string | null;
    } | null;
    scheduled_at_utc?: string | null;
    effective_start_time?: string | null;
  } | null;
  start_time?: string | null;
  schedule?: { time?: string | null } | null;
};

export function formatWorkoutDualScheduleTimeLabel(
  workout: WorkoutTimezoneContextSource | null | undefined,
  options: { role?: "trainer" | "client" } = {},
): DualScheduleTimeLabel {
  const scheduleContext = workout?.schedule_context;
  const timezoneContext = scheduleContext?.timezone_context;
  if (timezoneContext) {
    return formatDualScheduleTimeLabel({
      ...timezoneContext,
      scheduled_at_utc: timezoneContext.scheduled_at_utc ?? scheduleContext?.scheduled_at_utc ?? null,
    }, options);
  }

  const fallbackTime = String(
    workout?.schedule?.time
    ?? workout?.schedule_context?.effective_start_time
    ?? workout?.start_time
    ?? "",
  ).trim().slice(0, 5);

  return {
    primary: fallbackTime,
    secondary: "",
    secondaryDate: "",
    timezonesDiffer: false,
    datesDiffer: false,
    primaryTzLabel: "",
    secondaryTzLabel: "",
  };
}

export function formatDualScheduleTimeLabel(
  timezoneContext: {
    viewer?: TimezoneParty | null;
    client?: TimezoneParty | null;
    trainer?: TimezoneParty | null;
    canonical?: TimezoneParty | null;
    scheduled_at_utc?: string | null;
  } | null | undefined,
  options: { role?: "trainer" | "client" } = {},
): DualScheduleTimeLabel {
  const viewer = timezoneContext?.viewer ?? null;
  const client = timezoneContext?.client ?? timezoneContext?.canonical ?? null;
  const trainer = timezoneContext?.trainer ?? null;
  const scheduledAtUtc = String(timezoneContext?.scheduled_at_utc ?? "").trim() || null;
  const role = options.role ?? "trainer";

  const timezonesDiffer = Boolean(
    client?.iana
    && trainer?.iana
    && !isSameTimezone(client.iana, trainer.iana),
  );

  const trainerDate = String(trainer?.local_date ?? "").trim();
  const clientDate = String(client?.local_date ?? "").trim();
  const datesDiffer = Boolean(trainerDate && clientDate && trainerDate !== clientDate);

  let primaryParty: TimezoneParty | null;
  let secondaryParty: TimezoneParty | null;

  if (role === "client") {
    primaryParty = client;
    secondaryParty = timezonesDiffer ? trainer : null;
  } else {
    primaryParty = viewer ?? trainer ?? client;
    secondaryParty = timezonesDiffer ? client : null;
  }

  const primaryTime = resolvePartyScheduleTime(primaryParty, scheduledAtUtc);
  const secondaryTime = secondaryParty
    ? resolvePartyScheduleTime(secondaryParty, scheduledAtUtc)
    : "";

  const secondaryDateIso = timezonesDiffer && datesDiffer
    ? String(secondaryParty?.local_date ?? "").trim()
    : "";
  const secondaryDate = secondaryDateIso
    ? formatTrainingDateShortLabel(secondaryDateIso)
    : "";

  return {
    primary: primaryTime,
    secondary: secondaryTime,
    secondaryDate,
    timezonesDiffer,
    datesDiffer,
    primaryTzLabel: "",
    secondaryTzLabel: "",
  };
}

export function convertScheduleWallClockToTimezone(
  dateYmd: string,
  timeRaw: string,
  fromTimezone: string,
  toTimezone: string,
): { date: string; time: string } | null {
  const utc = utcFromLocalParts(dateYmd, timeRaw, fromTimezone);
  if (!utc) {
    return null;
  }
  const parts = localPartsFromUtc(utc, toTimezone);
  if (!parts) {
    return null;
  }
  return {
    date: parts.date,
    time: parts.time.slice(0, 5),
  };
}

export function utcFromLocalParts(
  dateYmd: string,
  timeRaw: string,
  timezone: string,
): string | null {
  const date = String(dateYmd ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return null;
  }
  let time = String(timeRaw ?? "").trim();
  if (!time || time === "--:--") {
    time = "00:00";
  }
  if (/^\d{2}:\d{2}$/.test(time)) {
    time = `${time}:00`;
  }

  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  try {
    const probe = new Date(`${date}T12:00:00Z`);
    const utcOffsetFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    void utcOffsetFormatter.format(probe);

    const localAsUtc = Date.parse(`${date}T${time}`);
    if (Number.isNaN(localAsUtc)) {
      return null;
    }

    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const targetParts = {
      year: date.slice(0, 4),
      month: date.slice(5, 7),
      day: date.slice(8, 10),
      hour: time.slice(0, 2),
      minute: time.slice(3, 5),
      second: time.length >= 8 ? time.slice(6, 8) : "00",
    };

    let guess = Date.parse(`${date}T${time}Z`);
    for (let i = 0; i < 3; i += 1) {
      const parts = formatter.formatToParts(new Date(guess));
      const read = Object.fromEntries(parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]));
      const formatted = `${read.year}-${read.month}-${read.day}T${read.hour}:${read.minute}:${read.second}Z`;
      const desired = `${targetParts.year}-${targetParts.month}-${targetParts.day}T${targetParts.hour}:${targetParts.minute}:${targetParts.second}Z`;
      if (formatted === desired) {
        return new Date(guess).toISOString().replace(".000Z", "Z").replace("T", " ").slice(0, 19);
      }
      guess += Date.parse(desired) - Date.parse(formatted);
    }

    return new Date(guess).toISOString().slice(0, 19).replace("T", " ");
  } catch {
    return null;
  }
}

export function localPartsFromUtc(
  utcDatetime: string,
  timezone: string,
): { date: string; time: string } | null {
  const raw = String(utcDatetime ?? "").trim();
  if (!raw) {
    return null;
  }
  const tz = normalizeIanaTimezone(timezone) ?? resolveDefaultTimezone();
  try {
    const instant = new Date(raw.includes("T") ? raw : `${raw.replace(" ", "T")}Z`);
    if (Number.isNaN(instant.getTime())) {
      return null;
    }
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(instant);
    const read = Object.fromEntries(parts.filter((p) => p.type !== "literal").map((p) => [p.type, p.value]));
    return {
      date: `${read.year}-${read.month}-${read.day}`,
      time: `${read.hour}:${read.minute}:${read.second}`,
    };
  } catch {
    return null;
  }
}

export function isSameTimezone(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = normalizeIanaTimezone(left);
  const b = normalizeIanaTimezone(right);
  return Boolean(a && b && a === b);
}
